import React, { useState } from "react";
// import { ArrowRight } from "lucide-react";
import { formatCurrency, formatNumber } from "./api.js";
import { ApiError, ForecastCell, ForecastLegend, SectionSkeleton,
    NewBadge, SeasonalBadge, RankBadge,DataTable } from '../utils/analyticsHelpers';
 
export default function ProductRanking({ query, onFilterChange }) {
  const isLoading = query.loading;
  const hasData = !!query.data?.rows?.length;
  const view = query.data?.view || "family";
  const totals = query.data?.totals || {};
  const other = query.data?.other || null;

  const [pageSize, setPageSize] = useState(10);

  if (query.error) return <ApiError error={query.error} label="Product Ranking" />;

  const allRows = query.data?.rows || [];
  const visibleRows = allRows.slice(0, pageSize);

  // ✅ Get grand totals for % calculation
  const grandTotalRevenue = Number(totals.revenue) || 0;
  const grandTotalUnits = Number(totals.unitsSold) || 0;

  // Helper to calculate total units based on qtyUn multiplier
  const calculateTotalUnits = (val, qtyUn) => {
    const multiplier = parseFloat(qtyUn);
    if (isNaN(multiplier)) return val;
    return val * multiplier;
  };

  // ✅ Safe percentage helper
  const calcPercent = (value, total) => {
    if (!total || total === 0) return 0;
    return ((Number(value) || 0) / total) * 100;
  };

  // 1. Map regular data rows
  const tableRows = visibleRows.map((p) => {
    const totalUnitsSold = calculateTotalUnits(p.unitsSold, p.qtyUn);
    const revenuePct = calcPercent(p.revenue, grandTotalRevenue);
    const unitsPct = calcPercent(totalUnitsSold, grandTotalUnits);

    return {
      rank: <RankBadge n={p.rank} />,
      ...(view === "family"
        ? {
            name: (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onFilterChange({ family: p.name })}
                  className="text-slate-800 font-medium hover:text-indigo-600 hover:underline text-left uppercase text-xs"
                >
                  {p.name}
                </button>
                {p.hasNewProducts && <NewBadge />}
                {p.hasSeasonalProducts && <SeasonalBadge />}
              </div>
            ),
            skuCount: <p className="text-slate-500 text-xs">{p.skuCount} SKUs</p>,
          }
        : {
            sku: <code className="text-xs bg-slate-100 px-1 rounded border border-slate-200">{p.sku}</code>,
            category: (
              <div className="text-slate-800 font-medium">
                {p.category}
                {p.firstSaleDate && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Since {new Date(p.firstSaleDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                    {" · "}{p.daysSinceLaunch}d
                  </div>
                )}
              </div>
            ),
            tags: (
              <div className="flex items-center gap-1">
                {p.isNewProduct && <NewBadge />}
                {p.isSeasonal && <SeasonalBadge />}
                {!p.isNewProduct && !p.isSeasonal && <span className="text-[9px] font-bold uppercase text-slate-400">Established</span>}
              </div>
            ),
          }),

      revenue: (
        <div className="text-right">
          <div className="text-slate-900">{formatCurrency(p.revenue)}</div>
          <div className="text-[10px] font-bold text-indigo-600 mt-0.5">{revenuePct.toFixed(2)}%</div>
        </div>
      ),
      unitsSold: (
        <div className="text-right">
          <div className="font-bold text-slate-900">{formatNumber(totalUnitsSold)}</div>
          <div className="text-[10px] font-bold text-indigo-600 mt-0.5">{unitsPct.toFixed(2)}%</div>
        </div>
      ),
      forecastUnits: <ForecastCell value={calculateTotalUnits(p.forecastUnits, p.qtyUn)} confidence={p.forecastConfidence} method={p.forecastMethod} kind="units" />,
      forecastRevenue: <ForecastCell value={p.forecastRevenue} confidence={p.forecastConfidence} method={p.forecastMethod} kind="revenue" />,
      projectedAnnualUnits: (
        <div className="text-right">
          <span className="font-semibold text-slate-900">{formatNumber(calculateTotalUnits(p.projectedAnnualUnits, p.qtyUn) || 0)}</span>
        </div>
      ),
      projectedAnnualRevenue: <span className="font-semibold text-emerald-700">{formatCurrency(p.projectedAnnualRevenue || 0)}</span>,
    };
  });

  // 2. DYNAMIC SUMMARY CALCULATION (Visible Items Only)
  const visibleSummary = visibleRows.reduce((acc, r) => {
    acc.revenue += (Number(r.revenue) || 0);
    acc.unitsSold += calculateTotalUnits(r.unitsSold, r.qtyUn);
    acc.fUnits += calculateTotalUnits(r.forecastUnits, r.qtyUn);
    acc.fRev += (Number(r.forecastRevenue) || 0);
    acc.pUnits += calculateTotalUnits(r.projectedAnnualUnits, r.qtyUn);
    acc.pRev += (Number(r.projectedAnnualRevenue) || 0);
    return acc;
  }, { revenue: 0, unitsSold: 0, fUnits: 0, fRev: 0, pUnits: 0, pRev: 0 });

  if (hasData && !isLoading) {
    // ✅ Add Dynamic Summary Row
    tableRows.push({
      isSummaryRow: true,
      rank: "",
      name: <span className="font-bold text-indigo-600 uppercase text-[10px]">Summary (Top {pageSize})</span>,
      revenue: <div className="text-right font-bold text-indigo-600">{formatCurrency(visibleSummary.revenue)}</div>,
      unitsSold: <div className="text-right font-bold text-indigo-600">{formatNumber(visibleSummary.unitsSold)}</div>,
      forecastUnits: <div className="text-right text-indigo-400 font-medium">{formatNumber(visibleSummary.fUnits)}</div>,
      forecastRevenue: <div className="text-right text-indigo-400 font-medium">{formatCurrency(visibleSummary.fRev)}</div>,
      projectedAnnualUnits: <div className="text-right text-indigo-500 font-medium">{formatNumber(visibleSummary.pUnits)}</div>,
      projectedAnnualRevenue: <div className="text-right text-indigo-500 font-medium">{formatCurrency(visibleSummary.pRev)}</div>,
    });

    // ✅ Add "Other" Row (Items past top 50 in DB)
    if (other && view === "family") {
      tableRows.push({
        isOtherRow: true,
        rank: "",
        name: <span className="font-black font-bold text-slate-900 text-[10px]">Other Families (Remaining)</span>,
        revenue: <div className="font-black text-right font-bold text-slate-900 italic text-xs">{formatCurrency(other.revenue || 0)}</div>,
        unitsSold: <div className="font-black text-right font-bold text-slate-900 italic text-xs">{formatNumber(other.unitsSold || 0)}</div>,
        forecastUnits: <div className="font-black text-right font-bold text-slate-900 italic text-[10px]">{formatNumber(other.forecastUnits || 0)}</div>,
        forecastRevenue: <div className="font-black text-right font-bold text-slate-900 italic text-[10px]">{formatCurrency(other.forecastRevenue || 0)}</div>,
        projectedAnnualUnits: <div className="font-black text-right font-bold text-slate-900 italic text-[10px]">{formatNumber(other.projectedAnnualUnits || 0)}</div>,
        projectedAnnualRevenue: <div className="font-black text-right font-bold text-slate-900 italic text-[10px]">{formatCurrency(other.projectedAnnualRevenue || 0)}</div>,
      });
    }

    // ✅ Add Grand Totals Row
    tableRows.push({
      isTotalsRow: true,
      rank: "",
      ...(view === "family"
        ? { name: <span className="font-black text-slate-900 uppercase text-xs">Grand Total</span> }
        : { sku: <span className="font-black text-slate-900 uppercase text-xs">Total</span>, category: "", tags: "" }),
      revenue: <div className="text-right font-black text-slate-900">{formatCurrency(totals.revenue || 0)}</div>,
      unitsSold: <div className="text-right font-black text-slate-900">{formatNumber(totals.unitsSold || 0)}</div>,
      forecastUnits: <div className="text-right font-bold text-slate-700">{formatNumber(totals.forecastUnits || 0)}</div>,
      forecastRevenue: <div className="text-right font-bold text-slate-700">{formatCurrency(totals.forecastRevenue || 0)}</div>,
      projectedAnnualUnits: <div className="text-right font-bold text-slate-900">{formatNumber(totals.projectedAnnualUnits || 0)}</div>,
      projectedAnnualRevenue: <div className="text-right font-bold text-emerald-800">{formatCurrency(totals.projectedAnnualRevenue || 0)}</div>,
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {view === "sku" && (
            <button onClick={() => onFilterChange({ family: undefined })} className="text-xs font-bold text-blue-600 hover:underline">
              ← Back to Product Families
            </button>
          )}
          {isLoading && <span className="text-[10px] font-bold text-amber-500 animate-pulse uppercase tracking-wider">• Syncing...</span>}
        </div>

        {view !== "sku" && (
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
            <span className="text-[9px] font-black text-slate-400 px-2 uppercase">Show Top</span>
            {[10, 25, 50].map((num) => (
              <button
                key={num}
                onClick={() => setPageSize(num)}
                className={`px-3 py-1 text-[10px] font-bold rounded transition-all ${
                  pageSize === num ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading && !hasData ? (
        <SectionSkeleton />
      ) : (
        <div className={isLoading ? "opacity-50 transition-opacity" : ""}>
          <DataTable
            columns={
              view === "family"
                ? [
                    { key: "rank", label: "#" },
                    { key: "name", label: "Product Family" },
                    { key: "skuCount", label: "SKU", align: "center" },
                    { key: "revenue", label: "Revenue", align: "right" },
                    { key: "unitsSold", label: "Units Sold", align: "right" },
                    { key: "forecastUnits", label: "Forecast UN", align: "right" },
                    { key: "forecastRevenue", label: "Forecast $", align: "right" },
                    { key: "projectedAnnualUnits", label: "Projected Annual UN", align: "right" },
                    { key: "projectedAnnualRevenue", label: "Projected Annual $", align: "right" },
                  ]
                : [
                    { key: "rank", label: "#" },
                    { key: "sku", label: "SKU" },
                    { key: "category", label: "Category" },
                    { key: "tags", label: "Type" },
                    { key: "revenue", label: "Revenue", align: "right" },
                    { key: "unitsSold", label: "Units Sold", align: "right" },
                    { key: "forecastUnits", label: "Forecast UN", align: "right" },
                    { key: "forecastRevenue", label: "Forecast $", align: "right" },
                    { key: "projectedAnnualUnits", label: "Projected Annual UN", align: "right" },
                    { key: "projectedAnnualRevenue", label: "Projected Annual $", align: "right" },
                  ]
            }
            rows={tableRows}
          />
          {hasData && <ForecastLegend />}
        </div>
      )}
    </div>
  );
}    

function ProductRankingBkp({ query, onFilterChange }) {
  const isLoading = query.loading;
  const hasData = !!query.data?.rows?.length;
  const view = query.data?.view || "family";
  const totals = query.data?.totals || {}; // Access totals from the JSON
  // Look for a line that looks like this and add 'other'
  const other = query.data?.other || {};

  const [pageSize, setPageSize] = useState(10);

  if (query.error) return <ApiError error={query.error} label="Product Ranking" />;

  const allRows = query.data?.rows || [];
  const visibleRows = allRows.slice(0, pageSize);

  // ✅ Get grand totals for % calculation
  const grandTotalRevenue = Number(totals.revenue) || 0;
  const grandTotalUnits = Number(totals.unitsSold) || 0;

  // Helper to calculate total units based on qtyUn multiplier
  const calculateTotalUnits = (val, qtyUn) => {
    const multiplier = parseFloat(qtyUn);
    if (isNaN(multiplier)) return val;
    return val * multiplier;
  };

  // ✅ NEW: Safe percentage helper
  const calcPercent = (value, total) => {
    if (!total || total === 0) return 0;
    return ((Number(value) || 0) / total) * 100;
  };

  // 1. Map regular data rows
  const tableRows = visibleRows.map((p) => {
    const totalUnitsSold = calculateTotalUnits(p.unitsSold, p.qtyUn);
    const totalForecastUnits = calculateTotalUnits(p.forecastUnits, p.qtyUn);
    const totalProjectedUnits = calculateTotalUnits(p.projectedAnnualUnits, p.qtyUn);
    const hasMultiplier = !isNaN(parseFloat(p.qtyUn));

    // ✅ Calculate % share for revenue and units
    const revenuePct = calcPercent(p.revenue, grandTotalRevenue);
    const unitsPct = calcPercent(totalUnitsSold, grandTotalUnits);

    return {
      rank: <RankBadge n={p.rank} />,
      ...(view === "family"
        ? {
            name: (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onFilterChange({ family: p.name })}
                  className="text-slate-800 font-medium hover:text-indigo-600 hover:underline text-left uppercase text-xs"
                >
                  {p.name}
                </button>
                {p.hasNewProducts && <NewBadge />}
                {p.hasSeasonalProducts && <SeasonalBadge />}
              </div>
            ),
            skuCount: <p className="text-slate-500 text-xs">{p.skuCount} SKUs</p>,
          }
        : {
            sku: (
              <code className="text-xs bg-slate-100 px-1 rounded border border-slate-200">
                {p.sku}
              </code>
            ),
            category: (
              <div className="text-slate-800 font-medium">
                {p.category}
                {p.firstSaleDate && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Since{" "}
                    {new Date(p.firstSaleDate).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })}
                    {" · "}
                    {p.daysSinceLaunch}d
                  </div>
                )}
              </div>
            ),
            tags: (
              <div className="flex items-center gap-1">
                {p.isNewProduct && <NewBadge />}
                {p.isSeasonal && <SeasonalBadge />}
                {!p.isNewProduct && !p.isSeasonal && (
                  <span className="text-[9px] font-bold uppercase text-slate-400">
                    Established
                  </span>
                )}
              </div>
            ),
          }),

      // ✅ REVENUE with % below
      revenue: (
        <div className="text-right">
          <div className="text-slate-900">{formatCurrency(p.revenue)}</div>
          <div className="text-[10px] font-bold text-indigo-600 mt-0.5">
            {revenuePct.toFixed(2)}%
          </div>
        </div>
      ),
      // ✅ UNITS with % below
      unitsSold: (
        <div className="text-right">
          <div className="font-bold text-slate-900">{formatNumber(totalUnitsSold)}</div>
          <div className="text-[10px] font-bold text-indigo-600 mt-0.5">
            {unitsPct.toFixed(2)}%
          </div>
          {view === "sku" && hasMultiplier && (
            <div className="text-[10px] font-bold text-slate-400 mt-0.5">
              {formatNumber(p.unitsSold)} × {p.qtyUn}
            </div>
          )}
        </div>
      ),
      forecastUnits: (
        <ForecastCell
          value={totalForecastUnits}
          confidence={p.forecastConfidence}
          method={p.forecastMethod}
          kind="units"
        />
      ),
      forecastRevenue: (
        <ForecastCell
          value={p.forecastRevenue}
          confidence={p.forecastConfidence}
          method={p.forecastMethod}
          kind="revenue"
        />
      ),
      projectedAnnualUnits: (
        <div className="text-right">
          <span className="font-semibold text-slate-900">
            {formatNumber(totalProjectedUnits || 0)}
          </span>
        </div>
      ),
      projectedAnnualRevenue: (
        <span className="font-semibold text-emerald-700">
          {formatCurrency(p.projectedAnnualRevenue || 0)}
        </span>
      ),
    };
  });

    // 1. Push "Other" row ONLY if it exists and we are in family view
    if (other && view === "family") {
    tableRows.push({
        isOtherRow: true,
        rank: "",
        name: (
        <span className="italic text-slate-500 font-medium">
            Other Families
        </span>
        ),
        revenue: (
        <span className="text-slate-600 font-medium">
            {formatCurrency(other.revenue || 0)}
        </span>
        ),
        unitsSold: (
        <span className="text-slate-600 font-medium">
            {formatNumber(other.unitsSold || 0)}
        </span>
        ),
        forecastUnits: (
        <span className="text-slate-500 italic">
            {formatNumber(other.forecastUnits || 0)}
        </span>
        ),
        forecastRevenue: (
        <span className="text-slate-500 italic">
            {formatCurrency(other.forecastRevenue || 0)}
        </span>
        ),
        projectedAnnualUnits: (
        <span className="text-slate-500 italic">
            {formatNumber(other.projectedAnnualUnits || 0)}
        </span>
        ),
        projectedAnnualRevenue: (
        <span className="text-slate-500 italic">
            {formatCurrency(other.projectedAnnualRevenue || 0)}
        </span>
        ),
    });
    }

    // 2. Append Totals Row if data exists
    if (hasData && !isLoading) {
    tableRows.push({
        isTotalsRow: true, // Custom flag if your DataTable supports specific styling
        rank: "",
        ...(view === "family"
        ? {
            // name: <span className="font-black text-slate-900 uppercase">Grand Total</span>,
            // skuCount: <span className="text-[10px] font-bold text-slate-400">{totals.orders} Orders</span>,
            }
        : {
            sku: <span className="font-black text-slate-900 uppercase">Total</span>,
            category: "",
            tags: "",
            }),
        revenue: <span className="font-black text-slate-900">{formatCurrency(totals.revenue || 0)}</span>,
        unitsSold: <span className="font-black text-slate-900">{formatNumber(totals.unitsSold || 0)}</span>,
        forecastUnits: <span className="font-bold text-slate-700">{formatNumber(totals.forecastUnits || 0)}</span>,
        forecastRevenue: <span className="font-bold text-slate-700">{formatCurrency(totals.forecastRevenue || 0)}</span>,
        projectedAnnualUnits: <span className="font-bold text-slate-900">{formatNumber(totals.projectedAnnualUnits || 0)}</span>,
        projectedAnnualRevenue: <span className="font-bold text-emerald-800">{formatCurrency(totals.projectedAnnualRevenue || 0)}</span>,
    });
    }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {view === "sku" && (
            <button
              onClick={() => onFilterChange({ family: undefined })}
              className="text-xs font-bold text-blue-600 hover:underline"
            >
              ← Back to Product Families
            </button>
          )}

          {isLoading && (
            <span className="text-[10px] font-bold text-amber-500 animate-pulse uppercase tracking-wider">
              • Syncing...
            </span>
          )}
        </div>

        {view !== "sku" && (
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
            <span className="text-[9px] font-black text-slate-400 px-2 uppercase">Show Top</span>
            {[10, 25, 50].map((num) => (
              <button
                key={num}
                onClick={() => setPageSize(num)}
                className={`px-3 py-1 text-[10px] font-bold rounded transition-all ${
                  pageSize === num
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading && !hasData ? (
        <SectionSkeleton />
      ) : (
        <div className={isLoading ? "opacity-50 transition-opacity" : ""}>
          <DataTable
            columns={
              view === "family"
                ? [
                    { key: "rank", label: "#" },
                    { key: "name", label: "Product Family" },
                    { key: "skuCount", label: "SKU", align: "center" },
                    { key: "revenue", label: "Revenue", align: "right" },
                    { key: "unitsSold", label: "Units Sold", align: "right" },
                    { key: "forecastUnits", label: "Forecast UN", align: "right" },
                    { key: "forecastRevenue", label: "Forecast $", align: "right" },
                    { key: "projectedAnnualUnits", label: "Projected Annual UN", align: "right" },
                    { key: "projectedAnnualRevenue", label: "Projected Annual $", align: "right" },
                  ]
                : [
                    { key: "rank", label: "#" },
                    { key: "sku", label: "SKU" },
                    { key: "category", label: "Category" },
                    { key: "tags", label: "Type" },
                    { key: "revenue", label: "Revenue", align: "right" },
                    { key: "unitsSold", label: "Units Sold", align: "right" },
                    { key: "forecastUnits", label: "Forecast UN", align: "right" },
                    { key: "forecastRevenue", label: "Forecast $", align: "right" },
                    { key: "projectedAnnualUnits", label: "Projected Annual UN", align: "right" },
                    { key: "projectedAnnualRevenue", label: "Projected Annual $", align: "right" },
                  ]
            }
            rows={tableRows} // Use the new tableRows array containing the footer
          />
          {hasData && <ForecastLegend />}
        </div>
      )}
    </div>
  );
}